// Shared scheduler for realtime-driven refetches (2026-09-09 incident, Phase 1).
//
// Every dashboard hook used to answer a realtime poke by re-running its full
// query suite on an 800-1500ms debounce. During a bulk upload that pattern
// multiplied ~580 document writes into hundreds of thousands of aggregate
// executions across every open staff tab — enough sustained load to drain the
// DB instance's burst credits and crash-loop Postgres (see
// notes/2026-09-09-db-crash-loop.md). The contract here caps what one open tab
// can cost:
//
//   - pokes coalesce into ONE reload per `delayMs` window (default 30s);
//   - a hidden tab NEVER reloads — pokes just mark it dirty;
//   - returning to the tab (visibilitychange/focus) flushes a dirty mark
//     immediately, so the user still sees fresh data the moment they look.
//
// Deliberately NOT configurable below 2s: sub-second freshness on aggregate
// views is indistinguishable to the user and was the whole problem.
export const REALTIME_RELOAD_DELAY_MS = 30_000

export type RealtimeReloadScheduler = {
  /** Signal that underlying data changed. Cheap; call as often as you like. */
  poke: () => void
  /** Detach listeners and cancel any pending reload. */
  dispose: () => void
}

export function createRealtimeReloadScheduler(
  run: () => void,
  { delayMs = REALTIME_RELOAD_DELAY_MS }: { delayMs?: number } = {},
): RealtimeReloadScheduler {
  let timer: ReturnType<typeof setTimeout> | null = null
  let dirty = false
  let disposed = false

  const fire = () => {
    timer = null
    if (disposed) return
    if (document.hidden) {
      // Woken while hidden: don't spend a query on a tab nobody is looking at.
      dirty = true
      return
    }
    dirty = false
    run()
  }

  const poke = () => {
    if (disposed) return
    if (document.hidden) {
      dirty = true
      return
    }
    if (timer) return
    timer = setTimeout(fire, delayMs)
  }

  // Back to the tab with changes pending → refresh right away (once).
  const flushIfDirty = () => {
    if (disposed || !dirty || document.hidden) return
    dirty = false
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    run()
  }

  document.addEventListener('visibilitychange', flushIfDirty)
  window.addEventListener('focus', flushIfDirty)

  return {
    poke,
    dispose: () => {
      disposed = true
      if (timer) clearTimeout(timer)
      timer = null
      document.removeEventListener('visibilitychange', flushIfDirty)
      window.removeEventListener('focus', flushIfDirty)
    },
  }
}
